import { createContext, use, useCallback, useEffect, useMemo, useState } from "react";
import {
  api,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
  setSessionLostHandler,
} from "../api/client";

export interface User {
  id: string;
  phone: string;
  name: string | null;
}

interface AuthValue {
  user: User | null;
  ready: boolean;
  login: (phone: string, password: string) => Promise<void>;
  register: (phone: string, password: string, name?: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

interface TokenPair {
  user: User;
  accessToken: string;
  refreshToken: string;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  // `ready` gates the router: without it a refresh on a protected route
  // bounces to /login for a frame before the session is restored.
  const [ready, setReady] = useState(false);

  const accept = useCallback((body: TokenPair) => {
    setAccessToken(body.accessToken);
    setRefreshToken(body.refreshToken);
    setUser(body.user);
  }, []);

  const clear = useCallback(() => {
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
  }, []);

  // Restore the session on load. The stored refresh token is the only thing
  // that survives a reload, so trade it for an access token immediately.
  useEffect(() => {
    setSessionLostHandler(() => setUser(null));

    (async () => {
      if (!getRefreshToken()) return setReady(true);
      try {
        const { user } = await api<{ user: User }>("/api/auth/me");
        setUser(user);
      } catch {
        clear();
      } finally {
        setReady(true);
      }
    })();
  }, [clear]);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      ready,
      login: async (phone, password) =>
        accept(
          await api<TokenPair>("/api/auth/login", {
            method: "POST",
            body: { phone, password },
            auth: false,
          }),
        ),
      register: async (phone, password, name) =>
        accept(
          await api<TokenPair>("/api/auth/register", {
            method: "POST",
            body: { phone, password, name: name || undefined },
            auth: false,
          }),
        ),
      logout: async () => {
        const refreshToken = getRefreshToken();
        clear();
        if (refreshToken) {
          // Best effort: the local session is already gone either way.
          await api("/api/auth/logout", {
            method: "POST",
            body: { refreshToken },
            auth: false,
          }).catch(() => {});
        }
      },
    }),
    [user, ready, accept, clear],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthValue {
  const ctx = use(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
